import { useIsMobile } from "@/hooks/use-mobile.tsx";
import type { ReactNode } from "react";
import { CanvasLayers } from "./CanvasLayers.tsx";
import { CanvasNavBoundary } from "./CanvasNavBoundary.tsx";
import type { HeaderUtilityLayer } from "@/components/layout/HeaderUtilitySlot.tsx";
import { CanvasActionContext, CanvasHeaderUtilitiesProvider } from "./canvasContext.tsx";
import { useEphemeralCanvas } from "./useEphemeralCanvas.ts";

/**
 * A canvas of this panel's own: an entity opened from inside presents as a card
 * over the panel rather than somewhere else on the screen.
 *
 * Mount it around a panel that shows an entity with links out of it — a contact
 * and its activity, say — wherever the panel is not itself a canvas card. Without
 * one, those links fall outward to whatever boundary is above: to the screen's
 * canvas, which opens the target in a *different* column beside this panel, and
 * with no canvas at all to a real navigation, which takes the panel off screen
 * entirely. Neither is what "open this event" means when the event is one row of
 * the thing already on screen.
 *
 * The wrapper is the box a card covers — layers are `absolute inset-0` against
 * the nearest positioned ancestor — so render it around the panel's whole frame,
 * header included, not around the scrolling body inside it.
 *
 * Mobile keeps its pages. There a panel already fills the screen, so a card over
 * it would be a page wearing a dialog's frame, and the back gesture the platform
 * gives is the one users reach for; a canvas here would take it away.
 */
export const LocalCanvasHost = ({
  children,
  headerUtilityLayer = "page",
}: {
  children: ReactNode;
  /**
   * The layer this panel's own header publishes the utilities at, so a card
   * covering that header can take them over at the same one. Defaults to the
   * page column, which is where a panel with a canvas of its own usually sits.
   *
   * `null` while the panel is on its way off screen: its header has already
   * stopped offering a slot, and a card that kept offering one would carry the
   * controls out of view with it rather than hand them back.
   */
  headerUtilityLayer?: HeaderUtilityLayer | null;
}) => {
  const isMobile = useIsMobile();
  // Before the bail below, because hook order cannot depend on the viewport.
  const { canvasStack, isCanvasOpen, isFullscreen, canvasActions } = useEphemeralCanvas({
    initialDisplay: "full",
  });

  if (isMobile) return <>{children}</>;

  return (
    // Over whatever canvas the screen has, deliberately: this panel presents its
    // own links, and the context a card inside reads has to be this one so a
    // link in *that* card drills here too rather than reaching back out.
    <CanvasActionContext.Provider value={canvasActions}>
      {/* A link inside a presented card layers over it. The stack starts empty,
          so the first open is the root. */}
      <CanvasNavBoundary>
        <div className="relative flex min-h-0 flex-1 flex-col">
          {children}
          {/* These cards cover this panel rather than the screen, so they are
              not the screen's canvas column and must not outrank it. They do
              cover this panel's own header, though — the box they fill includes
              it — so they publish at the layer that header uses and win the tie
              by publishing later. Leaving them out altogether stranded the
              controls behind the scrim whenever no card was open elsewhere. */}
          <CanvasHeaderUtilitiesProvider value={headerUtilityLayer}>
            <CanvasLayers
              canvasStack={canvasStack}
              isCanvasOpen={isCanvasOpen}
              isFullscreen={isFullscreen}
            />
          </CanvasHeaderUtilitiesProvider>
        </div>
      </CanvasNavBoundary>
    </CanvasActionContext.Provider>
  );
};
